import { FC, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import { ReactComponent as GoogleIcon } from '@/assets/icons/google.svg';
import { ReactComponent as Logo } from '@/assets/icons/logo.svg';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';

const Wrap = styled.div`
  height: 100vh;
  display: flex;
`;

const InnerWrap = styled.div`
  position: relative;
  height: 100vh;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const Header = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  width: 44%;
  padding: 22px 0;
  text-align: center;
  width: 100%;
  border-bottom: 1px solid ${({ theme }) => theme.colors.grayscaleLight};
`;

const StyledLogo = styled(Logo)`
  height: 20px;
  width: auto;
`;

const Footer = styled(Text)`
  padding: 24px 15px;
  text-align: center;
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  opacity: 0.3;
  a {
    text-decoration: underline;
  }
`;

const Inner = styled.div`
  text-align: center;
`;

const Heading = styled(Text)`
  margin: 0 0 16px;
`;

const StyledText = styled(Text)`
  margin: 0 0 32px;
`;

const Button = styled.a`
  display: inline-flex;
  transition: 0.2s ease-out all;
  height: 56px;
  align-items: center;
  padding: 0 40px;
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  border-radius: 4px;
  &:hover {
    border-color: ${({ theme }) => theme.colors.gray};
  }
  &:active {
    border-color: ${({ theme }) => theme.colors.black};
  }
  svg {
    margin-right: 8px;
  }
`;

const BottomLogo = styled.span`
  margin-bottom: 16px;
  display: block;
`;

const LoginPage: FC = () => {
  const alerts = useAlerts();
  const [searchParams] = useSearchParams();

  const error = searchParams.get('error');

  useEffect(() => {
    switch (error) {
      case 'unknown':
        alerts.error();
        break;
      case 'unauthorized':
        alerts.error(
          'Unauthorized!',
          "You don't have permission to join Backbone, or your invitation might be expired. Please contact administrator.",
        );
        break;
      case 'account_disabled':
        alerts.error(
          'Account disabled',
          'Your account has been disabled. Please contact administrator.',
        );
        break;
    }
  }, [alerts, error]);
  return (
    <Wrap>
      <InnerWrap>
        <Header>
          <StyledLogo />
        </Header>
        <Inner>
          <Heading as="h1" $type="heading1">
            Hi, welcome to Backbone
          </Heading>
          <StyledText $color="gray">
            Please, sign in with Google credentials.
          </StyledText>
          <Button
            href={`${process.env.REACT_APP_API_ENDPOINT}/socialite/google`}
          >
            <GoogleIcon />
            <Text $type="button" as="span">
              Sign in with Google
            </Text>
          </Button>
        </Inner>
        <Footer $type="smallHeadline" $color="gray">
          <BottomLogo>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="90"
              height="16"
              viewBox="0 0 90 16"
              fill="none"
            >
              <path
                d="M9.48776 7.71143V7.62783C10.5118 7.02179 11.0133 6.01868 11.0133 4.6394C11.0133 2.27791 9.27878 0.835938 6.68741 0.835938H0V15.4646H6.89639C9.82213 15.4646 11.5567 13.8973 11.5567 11.2432C11.5567 9.52956 10.867 8.35927 9.48776 7.71143ZM2.84215 3.36461H6.26944C7.50244 3.36461 8.10848 3.90796 8.10848 4.97377C8.10848 6.03958 7.46064 6.64562 6.20675 6.64562H2.86305L2.84215 3.36461ZM6.45753 12.9151H2.84215V9.1534H6.45753C7.8995 9.1534 8.63093 9.94753 8.63093 11.0342C8.63093 12.1836 7.8995 12.9151 6.45753 12.9151Z"
                fill="#909090"
              />
              <path
                d="M17.8741 4.40918C16.4321 4.40918 15.3245 4.76445 14.5303 5.45409C14.1529 5.78382 13.8524 6.19243 13.6502 6.65103C13.448 7.10964 13.3489 7.60706 13.3601 8.10815H15.9932C15.9932 7.29312 16.662 6.70798 17.8114 6.70798C19.0652 6.70798 19.6922 7.35582 19.6922 8.29623V9.23665C18.9077 8.99408 18.0889 8.88114 17.268 8.90228C14.3423 8.90228 12.8794 10.198 12.8794 12.2669C12.8794 14.2522 14.3214 15.6524 16.6829 15.6524C18.0412 15.6524 19.0443 15.2135 19.7131 14.3358H19.7967L20.0057 15.4643H22.5134V8.27534C22.5134 5.99744 20.7371 4.40918 17.8741 4.40918ZM19.6922 11.431C19.6922 12.5386 18.7727 13.4372 17.3516 13.4372C16.2649 13.4372 15.6797 12.9147 15.6797 12.1415C15.6797 11.3683 16.3485 10.8667 17.5397 10.8667C18.2727 10.8551 19.0018 10.9754 19.6922 11.222V11.431Z"
                fill="#909090"
              />
              <path
                d="M28.6505 13.1864C27.0622 13.1864 26.2681 12.1415 26.2681 10.0308C26.2681 7.92007 27.0622 6.87516 28.6505 6.87516C28.9073 6.86167 29.1643 6.89919 29.4066 6.98555C29.6489 7.07191 29.8717 7.20541 30.0621 7.37833C30.2525 7.55126 30.4068 7.76019 30.516 7.99306C30.6252 8.22593 30.6872 8.47813 30.6985 8.7351H33.5197C33.3526 6.16462 31.6598 4.40918 28.6505 4.40918C26.9786 4.40918 25.7038 4.93163 24.7843 5.95564C23.8648 6.97965 23.405 8.35893 23.405 10.0308C23.405 11.7026 23.8648 13.1028 24.8052 14.1268C25.7456 15.1508 27.0413 15.6524 28.7132 15.6524C31.6389 15.6524 33.3734 13.9387 33.5406 11.3683H30.7194C30.6861 11.8837 30.4494 12.3648 30.0614 12.7058C29.6734 13.0467 29.1659 13.2196 28.6505 13.1864Z"
                fill="#909090"
              />
              <path
                d="M44.2092 4.80657V4.59759H40.9491L37.1665 8.81902V0H34.5125V15.4646H37.1665V10.5536L41.0536 15.4646H44.4182V15.2556L39.8206 9.63404L44.2092 4.80657Z"
                fill="#909090"
              />
              <path
                d="M51.1164 4.40951C50.5425 4.37687 49.9692 4.48333 49.4453 4.71983C48.9213 4.95633 48.4622 5.31588 48.1071 5.76789H48.0235V0H45.2231V15.4646H47.7309L48.0235 14.2943H48.1071C48.4622 14.7463 48.9213 15.1059 49.4453 15.3424C49.9692 15.5789 50.5425 15.6853 51.1164 15.6527C51.7407 15.6732 52.3608 15.5434 52.9245 15.2743C53.4882 15.0052 53.979 14.6046 54.3556 14.1062C55.1707 13.0822 55.5677 11.7239 55.5677 10.0311C55.6308 8.58505 55.2118 7.159 54.3765 5.97687C54.0016 5.47013 53.5087 5.06251 52.9406 4.78938C52.3725 4.51625 51.7463 4.38586 51.1164 4.40951ZM50.3223 13.1867C48.6713 13.1867 47.919 11.8701 47.919 10.0311C47.919 8.19207 48.6504 6.87549 50.3223 6.87549C51.9941 6.87549 52.7256 8.12938 52.7256 10.0311C52.7256 11.9328 51.9941 13.1867 50.3223 13.1867Z"
                fill="#909090"
              />
              <circle cx="62.1488" cy="10.1815" r="5.81819" fill="#909090" />
              <path
                d="M74.8973 4.54204C74.3066 4.51448 73.718 4.6314 73.1827 4.88267C72.6474 5.13394 72.1815 5.51198 71.8253 5.98401H71.7417L71.5327 4.75102H69.0249V15.5972H71.8253V9.57849C71.8253 8.05293 72.7239 7.07071 74.0614 7.07071C75.3988 7.07071 76.0676 7.80215 76.0676 9.24412V15.5972H78.8679V8.49179C78.847 6.0467 77.3215 4.54204 74.8973 4.54204Z"
                fill="#909090"
              />
              <path
                d="M84.9441 4.54135C83.2723 4.54135 81.9766 5.0847 81.078 6.15051C80.1569 7.28781 79.6815 8.72156 79.7405 10.1839C79.6826 11.6567 80.1826 13.0971 81.1407 14.2172C81.6279 14.7439 82.2248 15.1573 82.8892 15.4281C83.5536 15.6989 84.2693 15.8206 84.9859 15.7846C86.1753 15.8373 87.3472 15.4835 88.3087 14.7814C88.7233 14.4761 89.0733 14.0918 89.3387 13.6507C89.6041 13.2095 89.7797 12.7202 89.8552 12.211H87.1175C86.8389 13.0469 86.1284 13.4649 84.9859 13.4649C83.4186 13.4649 82.6244 12.5035 82.6035 10.9571H90.0015V9.87038C90.0469 8.47624 89.5614 7.11683 88.6431 6.06692C88.1761 5.55693 87.6027 5.15591 86.9635 4.89227C86.3242 4.62863 85.6348 4.50882 84.9441 4.54135ZM82.6244 9.07625C82.6871 7.75967 83.4395 6.88194 84.9441 6.88194C86.3443 6.88194 87.0757 7.75967 87.1384 9.07625H82.6244Z"
                fill="#909090"
              />
            </svg>
          </BottomLogo>
          © 2022 |{' '}
          <a
            href="https://surveys.hotjar.com/13dd9598-77c9-4013-90ec-ce073873adcd"
            target="_blank"
            rel="noreferrer noopener"
          >
            Share Feedback
          </a>
        </Footer>
      </InnerWrap>
    </Wrap>
  );
};

export default LoginPage;
